import { CreateCGSchemaRequest, DestinationSchemaQueryOpts, ZipApi } from '../../lib/ZipApi';
import { directoryStore } from '../DirectoryStore';
import { CGBaseTypeDefinition, CGContentType, CGObjectDefinition, CGProductInstance, CGSourceMetadata, ObjectDefinition, OptimizelyProduct, Target } from '../../types/DataSyncItem';
import { DataSyncObjectsStore } from './DataSyncObjectsStore';
import { showToasts } from '../../lib/toaster';
import { CGLanguage } from '../../types/ContentGraph';
import { action, observable } from 'mobx';
import { DataSyncObjectDefinitionService } from '../../services/DataSync/DataSyncObjectDefinitionService';
import { CGDestinationSchema, DestinationSchemaItem } from '../../types/Schema';
import { FieldDefinition } from '../../types/FieldDefinition';
import { convertSchemaV2ToObjectDefinition } from '../../lib/convertV2Schema';
import { getApiErrorMessage } from '../../lib/ZipApiError';
import { graphAuthStore, isGraphAuthPendingError } from '../../lib/graphAuth';

export default class DataSyncCgObjectsStore extends DataSyncObjectsStore {

  @observable public loadingSourceMetadata = false;
  @observable public loadingBaseTypes = false;
  @observable public savingNewLanguage = false;

  public preset: string | null = null;
  public productInstance: CGProductInstance | null = null;
  @observable private _destinationObjectList?: DestinationSchemaItem[];
  @observable private sourceMetadata?: CGSourceMetadata;
  @observable private baseTypes: CGBaseTypeDefinition[] = [];
  @observable private _requestContext: DestinationSchemaQueryOpts = {};
  // Graph source this sync writes to; sent on source-scoped calls (languages, content-type creation).
  @observable private _resolvedGraphSourceId?: string;

  @action
  public setRequestContext(ctx: DestinationSchemaQueryOpts): void {
    this._requestContext = ctx;
  }

  @action
  public setResolvedGraphSourceId(sourceId?: string): void {
    this._resolvedGraphSourceId = sourceId;
  }

  public getResolvedGraphSourceId(): string | undefined {
    return this._resolvedGraphSourceId;
  }

  /**
   * Returns the resolved Graph source id, fetching the destination-schema listing (which echoes
   * it) if not captured yet. Returns undefined when there is no request context.
   */
  @action
  public async ensureResolvedGraphSourceId(): Promise<string | undefined> {
    if (this._resolvedGraphSourceId) {
      return this._resolvedGraphSourceId;
    }
    if (!directoryStore.trackerId || !this.instanceId) {
      return undefined;
    }
    if (!this._requestContext.dataSyncId && !this._requestContext.sourceContext) {
      return undefined;
    }
    const response = await ZipApi.listDestinationSchemas(
      directoryStore.trackerId,
      'CGR',
      this.instanceId,
      this._requestContext
    );
    if (response?.graph_source_id) {
      this.setResolvedGraphSourceId(response.graph_source_id);
    }
    return this._resolvedGraphSourceId;
  }

  @action
  public clearDestinationCache(): void {
    this._destinationObjectList = undefined;
    this._destinationObjectDefinitions = undefined;
    this._resolvedGraphSourceId = undefined;
    // Languages and unsaved cloned types belong to the previous source; this store is shared
    // across syncs on the same Graph instance.
    this.sourceMetadata = undefined;
    this._transientDestinationObject = undefined;
  }

  @action
  public async fetchSourceMetadata() {
    if (!this.instanceId || !directoryStore.trackerId) {
      return;
    }

    // Don't call with a blank id: the backend would return `ocp`'s languages instead.
    const sourceId = this._resolvedGraphSourceId;
    if (!sourceId) {
      this.sourceMetadata = { locales: [] };
      return;
    }

    try {
      this.loadingSourceMetadata = true;
      this.sourceMetadata = undefined;
      this.sourceMetadata = await ZipApi.getCGSourceMetadata(
        directoryStore.trackerId,
        this.instanceId,
        sourceId
      );
    } catch (error: any) {
      const result = await this.handleGraphPendingOr(
        error,
        () => ZipApi.getCGSourceMetadata(directoryStore.trackerId!, this.instanceId!, sourceId),
        'Unable to fetch source languages'
      );
      if (result !== undefined) {
        this.sourceMetadata = result;
      }
    } finally {
      this.loadingSourceMetadata = false;
    }
  }

  @action
  public async fetchBaseTypes() {
    if (!this.instanceId || !directoryStore.trackerId) {
      return;
    }

    try {
      this.loadingBaseTypes = true;
      this.baseTypes = [];
      this.baseTypes = await ZipApi.listCGBaseTypes(directoryStore.trackerId, this.instanceId);
    } catch (error: any) {
      const result = await this.handleGraphPendingOr(
        error,
        () => ZipApi.listCGBaseTypes(directoryStore.trackerId!, this.instanceId!),
        'Unable to fetch graph base types'
      );
      if (result !== undefined) {
        this.baseTypes = result;
      }
    } finally {
      this.loadingBaseTypes = false;
    }
  }

  public getSourceMetadata(): CGSourceMetadata | undefined {
    return this.sourceMetadata;
  }

  public getBaseTypeDefinitions(): CGBaseTypeDefinition[] {
    return this.baseTypes;
  }

  @action
  public async updateSourceMetadata(additionalLanguages: [CGLanguage]) {
    if (!this.instanceId || !directoryStore.trackerId) {
      return;
    }
    // A blank id would update `ocp`'s languages on the backend.
    const sourceId = await this.ensureResolvedGraphSourceId();
    if (!sourceId) {
      throw new Error('Select a source for this sync before adding languages.');
    }
    try {
      this.savingNewLanguage = true;
      await ZipApi.updateCGSourceMetadata(
        directoryStore.trackerId,
        this.instanceId,
        additionalLanguages,
        sourceId
      );
      const existingLocales = this.sourceMetadata?.locales ? this.sourceMetadata.locales : [];
      this.sourceMetadata = {locales: existingLocales.concat(additionalLanguages)};
    } finally {
      this.savingNewLanguage = false;
    }
  }

  @action
  public async persistTransientDestinationObject(): Promise<void> {
    if (!this._transientDestinationObject) {
      return;
    }

    const contentType =  DataSyncObjectDefinitionService.convertToCGContentType(
      this._transientDestinationObject as CGObjectDefinition
    ) as CGContentType;

    // A blank id would create the type on the shared `ocp` source instead of the sync's source.
    const graphSourceId = await this.ensureResolvedGraphSourceId();
    if (!graphSourceId) {
      throw new Error(
        'Select a source for this sync before adding a content type, so the new type is created '
        + 'on the correct Graph source. Please pick the source, then try again.'
      );
    }

    const request = {
      'content_type': contentType,
      'preset': this.preset ? this.preset : 'NEXT',
      'tracker_id': directoryStore.trackerId,
      'instance_id': this.instanceId,
      'graph_source_id': graphSourceId
    } as CreateCGSchemaRequest;

    await ZipApi.saveCGSchema(request);

    if (!this._destinationObjectList) this._destinationObjectList = [];

    const listIndex = this._destinationObjectList.findIndex(
      (obj) => obj.id === this._transientDestinationObject!.object_name
    );

    if (listIndex === -1) {
      this._destinationObjectList.push({
        id: this._transientDestinationObject.object_name,
        display_name: this._transientDestinationObject.object_display_name,
      });
    }

    if (!this._destinationObjectDefinitions) this._destinationObjectDefinitions = [];

    const defIndex = this._destinationObjectDefinitions.findIndex(
      (obj) => obj.object_name === this._transientDestinationObject!.object_name
    );

    if (defIndex !== -1) {
      this._destinationObjectDefinitions[defIndex] = this._transientDestinationObject;
    } else {
      this._destinationObjectDefinitions.push(this._transientDestinationObject);
    }

    this.setTransientDestinationObject(undefined);
  }

  public getProductInstance(): CGProductInstance {
    if (this.productInstance) return this.productInstance;
    return {} as CGProductInstance;

  }

  public getInstanceId(): string | undefined {
    return this.instanceId;
  }

  public getDestinationObjectList(
    configuredObjectName?: string,
    configuredObjectDisplayName?: string
  ): ObjectDefinition[] {
    const objects = this._destinationObjectList?.map((item) => {
      return {
        object_name: item.id,
        object_display_name: item.id || item.display_name,
        object_icon_url: '',
        fields: [],
      };
    }) || [];

    // If configured object is provided and not in available list, prepend it as unavailable
    if (configuredObjectName && configuredObjectDisplayName) {
      const isAvailable = objects.some((obj) => obj.object_name === configuredObjectName);
      if (!isAvailable) {
        return [{
          object_name: configuredObjectName,
          object_display_name: configuredObjectDisplayName,
          object_icon_url: '',
          fields: [],
          disabled: true,
          warning: 'This content type is no longer available. Please select a different content type.'
        }, ...objects];
      }
    }

    return objects;
  }

  public isDestinationObjectAvailable(objectName: string): boolean {
    if (!objectName) {
      return true;
    }

    const inList = this._destinationObjectList?.some((item) => item.id === objectName) || false;

    const isTransient = this._transientDestinationObject?.object_name === objectName;

    return inList || isTransient;
  }

  public async getSourceObjectFields(objectName: string): Promise<FieldDefinition[]> {
    const object = this._sourceObjectDefinitions?.find((obj) => obj.object_name === objectName);
    if (!object) {
      return [];
    }
    if (object.fields.length > 0) {
      return object.fields;
    }

    if (!directoryStore.trackerId || !this.instanceId || !objectName) {
      return [];
    }

    if (!this.isSourceObjectAvailable(objectName)) {
      return [];
    }

    this.loading = true;
    try {
      const schema = await ZipApi.getSourceSchema(
        directoryStore.trackerId,
        OptimizelyProduct.CGR,
        this.instanceId,
        objectName
      );
      const objectDef = convertSchemaV2ToObjectDefinition(schema);
      if (objectDef?.fields) {
        object.fields = objectDef.fields;
        return objectDef.fields;
      }
    } catch (error: any) {
      await this.handleSourceFetchError(error);
    } finally {
      this.loading = false;
    }

    return [];
  }

  public async getDestinationObjectFields(objectName: string): Promise<FieldDefinition[]> {
    if (!directoryStore.trackerId || !this.instanceId || !objectName) {
      return [];
    }

    const transientDestinationObject = this.getTransientDestinationObject();
    if (transientDestinationObject?.object_name === objectName) {
      return transientDestinationObject?.fields || [];
    }

    // Don't fetch if object is not available
    if (!this.isDestinationObjectAvailable(objectName)) {
      return [];
    }

    this.loading = true;
    try {
      const schema = await ZipApi.getDestinationSchema(
        directoryStore.trackerId,
        'CGR',
        this.instanceId,
        objectName,
        this._requestContext
      );
      const objectDef = convertSchemaV2ToObjectDefinition(schema);
      if (objectDef?.fields) {
        return objectDef.fields;
      }
    } catch (error: any) {
      await this.handleDestinationFetchError(error);
    } finally {
      this.loading = false;
    }

    return [];
  }

  public async getDestinationObjectSchema(objectName: string): Promise<CGDestinationSchema | undefined> {
    if (!directoryStore.trackerId || !this.instanceId || !objectName) {
      return undefined;
    }

    if (!this.isDestinationObjectAvailable(objectName)) {
      return undefined;
    }

    this.loading = true;
    try {
      const schema = await ZipApi.getDestinationSchema(
        directoryStore.trackerId,
        'CGR',
        this.instanceId,
        objectName,
        this._requestContext
      );
      if (schema?.graph_source_id) {
        this.setResolvedGraphSourceId(schema.graph_source_id);
      }
      return schema;
    } catch (error: any) {
      await this.handleDestinationFetchError(error);
    } finally {
      this.loading = false;
    }

    return undefined;
  }

  protected async fetchSourceObjectDefinitions(): Promise<ObjectDefinition[]> {
    if (!directoryStore.trackerId || !this.instanceId) {
      return [];
    }
    const response = await ZipApi.listSourceSchemas(
      directoryStore.trackerId,
      OptimizelyProduct.CGR,
      this.instanceId
    );
    if (!response?.schemas) {
      return [];
    }
    return response.schemas.map((item) => ({
      object_name: item.id,
      object_display_name: item.display_name || item.id,
      object_icon_url: '',
      fields: [],
      disabled: item.disabled,
      warning: item.warning,
    }));
  }

  protected async fetchDestinationObjectDefinitions(): Promise<ObjectDefinition[]> {
    if (!directoryStore.trackerId || !this.instanceId) {
      return [];
    }
    const response = await ZipApi.listDestinationSchemas(
      directoryStore.trackerId,
      'CGR',
      this.instanceId,
      this._requestContext
    );
    if (response) {
      this._destinationObjectList = response.schemas || [];
      if (response.graph_source_id) {
        this.setResolvedGraphSourceId(response.graph_source_id);
      }
    }
    // Languages need the source id captured above.
    await this.fetchSourceMetadata();
    return this.getDestinationObjectList();
  }

  // The Graph destination schema endpoint needs an EDIT or CREATE request context.
  protected canFetch(): boolean {
    if (!super.canFetch()) {
      return false;
    }
    if (this.target === Target.DESTINATION) {
      return !!this._requestContext.dataSyncId || !!this._requestContext.sourceContext;
    }
    return true;
  }

  protected async handleSourceFetchError(error: any): Promise<void> {
    await this.handleGraphPendingOr(error, () => this.fetch(), 'Unable to fetch graph schema');
  }

  protected async handleDestinationFetchError(error: any): Promise<void> {
    await this.handleGraphPendingOr(error, () => this.fetch(), 'Unable to fetch graph schema');
  }

  protected async fetchAdditionalData() {
    // Languages are fetched after the destination list, which resolves the Graph source id.
    await Promise.all([
      this.fetchProductInstance(),
      this.fetchBaseTypes()
    ]);
  }

  private async fetchProductInstance(): Promise<void> {
    if (directoryStore.trackerId && this.instanceId) {
      this.productInstance = await ZipApi.getCGInstance(directoryStore.trackerId, this.instanceId);
    }
  }

  private async handleGraphPendingOr<T>(
    error: any,
    retry: () => Promise<T>,
    fallbackContext: string
  ): Promise<T | undefined> {
    const instanceId = this.instanceId;
    const trackerId = directoryStore.trackerId;
    if (instanceId && trackerId && (await isGraphAuthPendingError(error))) {
      try {
        await graphAuthStore.waitForGraphAuth(
          instanceId,
          () => ZipApi.getCGInstance(trackerId, instanceId)
        );
        return await retry();
      } catch {
        return undefined;
      }
    }
    const message = await getApiErrorMessage(error, fallbackContext);
    showToasts([{intent: 'danger', message}]);
    return undefined;
  }
}
